import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../components/common/SafeIcon';
import Navbar from '../components/nav/Navbar';
import { useAuth } from '../context/AuthContext';
import { useNewsletter } from '../hooks/useNewsletter';
import { useFanFollows } from '../hooks/useFollows';

const {
  FiMail, FiArrowLeft, FiCheck, FiLoader, FiAlertCircle,
  FiBell, FiCalendar, FiHeart, FiSliders, FiCheckCircle,
} = FiIcons;

const GENRES = ['Hip-Hop', 'R&B', 'Electronic', 'Pop', 'Alt-Pop', 'Neo-Soul', 'Jazz', 'Afrobeat'];

const Toggle = ({ checked, onChange, label, sub }) => (
  <label className="flex items-start gap-4 cursor-pointer group">
    <div className="relative mt-0.5 flex-shrink-0">
      <input type="checkbox" className="sr-only" checked={checked} onChange={e => onChange(e.target.checked)} />
      <div className={`w-11 h-6 rounded-full transition-all ${checked ? 'bg-brand-pink' : 'bg-zinc-800'}`} />
      <div className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-5' : ''}`} />
    </div>
    <div>
      <p className="text-sm font-black text-white uppercase tracking-wide">{label}</p>
      {sub && <p className="text-xs text-zinc-500 font-bold mt-0.5">{sub}</p>}
    </div>
  </label>
);

const NewsletterPreferences = () => {
  const { user, profile } = useAuth();
  const { subscription, loading, saving, error, saveSubscription, unsubscribe } = useNewsletter(user?.id, user?.email);
  const { follows } = useFanFollows(user?.id);

  const [email,         setEmail]         = useState('');
  const [isActive,      setIsActive]      = useState(true);
  const [frequency,     setFrequency]     = useState('weekly');
  const [followedOnly,  setFollowedOnly]  = useState(true);
  const [genres,        setGenres]        = useState([]);
  const [saved,         setSaved]         = useState(false);

  useEffect(() => {
    if (subscription) {
      setEmail(subscription.email || user?.email || '');
      setIsActive(subscription.is_active ?? true);
      setFrequency(subscription.frequency || 'weekly');
      setFollowedOnly(subscription.preferences?.followed_only ?? true);
      setGenres(subscription.preferences?.genres || []);
    } else if (user?.email) {
      setEmail(user.email);
    }
  }, [subscription, user]);

  const toggleGenre = (g) =>
    setGenres(prev => prev.includes(g) ? prev.filter(x => x !== g) : [...prev, g]);

  const handleSave = async () => {
    const result = await saveSubscription({
      email,
      isActive,
      frequency,
      preferences: { genres, followed_only: followedOnly },
    });
    if (result.success) {
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center space-y-4">
          <p className="text-white font-black text-xl uppercase">Sign in to manage your digest</p>
          <Link to="/auth" className="inline-block px-8 py-4 btn-brand rounded-2xl text-xs">Sign In</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <Navbar />

      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="ambient-orb-pink w-[600px] h-[600px] top-[-15%] right-[-10%] animate-caustic" />
        <div className="ambient-orb-coral w-[400px] h-[400px] bottom-[-10%] left-[-10%] animate-caustic" style={{ animationDelay: '3s' }} />
      </div>

      <div className="relative z-10 max-w-2xl mx-auto px-6 pt-32 pb-20 space-y-10">

        {/* Header */}
        <div className="space-y-6">
          <Link to="/profile" className="flex items-center gap-2 text-zinc-500 hover:text-white transition-colors group">
            <div className="w-8 h-8 rounded-xl glass-card flex items-center justify-center group-hover:border-brand-pink/30 transition-all">
              <SafeIcon icon={FiArrowLeft} className="text-sm" />
            </div>
            <span className="text-xs font-black uppercase tracking-widest">Back to Profile</span>
          </Link>

          <div className="flex items-start gap-5">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-brand-pink/20 to-brand-coral/20 border border-brand-pink/20 flex items-center justify-center flex-shrink-0">
              <SafeIcon icon={FiMail} className="text-2xl text-brand-pink" />
            </div>
            <div>
              <span className="text-xs font-black tracking-widest text-brand-pink uppercase">Email Digest</span>
              <h1 className="text-4xl font-black tracking-tighter uppercase text-white mt-1">Newsletter</h1>
              <p className="text-zinc-500 font-bold text-sm mt-1">
                Get weekly drops from artists you follow, delivered to your inbox.
              </p>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <SafeIcon icon={FiLoader} className="text-3xl text-brand-pink animate-spin" />
          </div>
        ) : (
          <div className="space-y-6">

            {/* Master toggle */}
            <div className="glass-card rounded-3xl p-6 space-y-5">
              <div className="flex items-center gap-3 border-b border-white/5 pb-5">
                <SafeIcon icon={FiBell} className="text-brand-pink" />
                <h2 className="text-xs font-black uppercase tracking-widest text-white">Subscription</h2>
              </div>
              <Toggle
                checked={isActive}
                onChange={setIsActive}
                label="Email Digest Active"
                sub="Receive periodic drop digests from Vision Direct"
              />

              {isActive && (
                <div className="space-y-4 pt-2">
                  <div className="space-y-2">
                    <label className="text-xs font-black uppercase tracking-widest text-zinc-400">Notification Email</label>
                    <input
                      type="email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      placeholder="your@email.com"
                      className="w-full px-4 py-3 bg-zinc-900 border border-zinc-800 rounded-2xl text-white text-sm font-bold focus:outline-none focus:border-brand-pink/50 transition-colors"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Frequency */}
            {isActive && (
              <div className="glass-card rounded-3xl p-6 space-y-5">
                <div className="flex items-center gap-3 border-b border-white/5 pb-5">
                  <SafeIcon icon={FiCalendar} className="text-brand-pink" />
                  <h2 className="text-xs font-black uppercase tracking-widest text-white">Frequency</h2>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { val: 'daily', label: 'Daily Digest', sub: 'Every day' },
                    { val: 'weekly', label: 'Weekly Digest', sub: 'Every Monday' },
                  ].map(({ val, label, sub }) => (
                    <button
                      key={val}
                      onClick={() => setFrequency(val)}
                      className={`p-4 rounded-2xl border text-left transition-all ${
                        frequency === val
                          ? 'border-brand-pink/50 bg-brand-pink/10'
                          : 'border-zinc-800 hover:border-zinc-700'
                      }`}
                    >
                      {frequency === val && (
                        <SafeIcon icon={FiCheckCircle} className="text-brand-pink text-sm mb-2" />
                      )}
                      <p className="text-xs font-black uppercase tracking-wide text-white">{label}</p>
                      <p className="text-[10px] text-zinc-500 font-bold mt-0.5">{sub}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Content filters */}
            {isActive && (
              <div className="glass-card rounded-3xl p-6 space-y-5">
                <div className="flex items-center gap-3 border-b border-white/5 pb-5">
                  <SafeIcon icon={FiSliders} className="text-brand-pink" />
                  <h2 className="text-xs font-black uppercase tracking-widest text-white">Content Filters</h2>
                </div>

                <Toggle
                  checked={followedOnly}
                  onChange={setFollowedOnly}
                  label="Followed Artists Only"
                  sub={`Show drops only from the ${follows.length} artist${follows.length !== 1 ? 's' : ''} you follow`}
                />

                {!followedOnly && (
                  <div className="space-y-3 pt-2">
                    <p className="text-xs font-black uppercase tracking-widest text-zinc-400">Filter by Genre</p>
                    <p className="text-[10px] text-zinc-600 font-bold">Leave empty to receive all genres</p>
                    <div className="flex flex-wrap gap-2">
                      {GENRES.map(g => (
                        <button
                          key={g}
                          onClick={() => toggleGenre(g)}
                          className={`px-3 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                            genres.includes(g)
                              ? 'bg-brand-pink/20 border border-brand-pink/40 text-brand-pink'
                              : 'glass-card text-zinc-500 hover:text-white hover:border-zinc-600'
                          }`}
                        >
                          {g}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Followed artists preview */}
            {isActive && follows.length > 0 && (
              <div className="glass-card rounded-3xl p-6 space-y-5">
                <div className="flex items-center gap-3 border-b border-white/5 pb-5">
                  <SafeIcon icon={FiHeart} className="text-brand-pink" />
                  <h2 className="text-xs font-black uppercase tracking-widest text-white">
                    Following ({follows.length})
                  </h2>
                </div>
                <div className="space-y-3 max-h-48 overflow-y-auto pr-1">
                  {follows.map(({ id, artist }) => (
                    <div key={id} className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl overflow-hidden bg-zinc-800 flex-shrink-0 border border-zinc-700">
                        {artist?.avatar_image
                          ? <img src={artist.avatar_image} alt={artist.name} className="w-full h-full object-cover" />
                          : <div className="w-full h-full flex items-center justify-center text-zinc-600 text-xs font-black">
                              {artist?.name?.[0] || '?'}
                            </div>
                        }
                      </div>
                      <div>
                        <p className="text-xs font-black text-white uppercase tracking-tight">{artist?.name}</p>
                        {artist?.genre && <p className="text-[10px] text-zinc-600 font-bold">{artist.genre}</p>}
                      </div>
                      {artist?.verified && (
                        <SafeIcon icon={FiCheck} className="text-blue-400 text-xs ml-auto" />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/30 rounded-2xl">
                <SafeIcon icon={FiAlertCircle} className="text-red-400 flex-shrink-0" />
                <p className="text-red-400 text-xs font-black">{error}</p>
              </div>
            )}

            {/* Success */}
            {saved && (
              <div className="flex items-center gap-3 p-4 bg-green-500/10 border border-green-500/30 rounded-2xl">
                <SafeIcon icon={FiCheckCircle} className="text-green-400 flex-shrink-0" />
                <p className="text-green-400 text-xs font-black">Preferences saved! Your digest is set up.</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 flex items-center justify-center gap-2 py-4 btn-brand rounded-2xl text-xs disabled:opacity-60"
              >
                {saving
                  ? <><SafeIcon icon={FiLoader} className="animate-spin" /> Saving…</>
                  : <><SafeIcon icon={FiCheck} /> Save Preferences</>
                }
              </button>
              {subscription?.is_active && (
                <button
                  onClick={unsubscribe}
                  disabled={saving}
                  className="px-6 py-4 border border-zinc-800 text-zinc-500 hover:text-red-400 hover:border-red-500/30 rounded-2xl text-xs font-black uppercase tracking-widest transition-all disabled:opacity-60"
                >
                  Unsubscribe
                </button>
              )}
            </div>

            {/* Info note */}
            <div className="text-center space-y-1">
              <p className="text-[10px] text-zinc-700 font-bold">
                Digests are sent from{' '}
                <span className="text-zinc-500 font-black">info@visionmedia345.com</span>
              </p>
              <p className="text-[10px] text-zinc-700 font-bold">
                You can unsubscribe at any time from this page or via the email link.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NewsletterPreferences;